import { useI18n } from '../../../i18n';
import { DataTable, Column, Pagination } from '../../../components/shared';
import { InventoryItem, InventoryCells } from './InventoryTableRow';

// ============================================
// Types
// ============================================

interface InventoryTableProps {
  data: InventoryItem[];
  isLoading?: boolean;
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRowClick?: (item: InventoryItem) => void;
  onRowActionClick?: (item: InventoryItem) => void;
  onFilterClick?: () => void;
  /**
   * Chọn dòng cho điều chỉnh hàng loạt. Bỏ trống cả hai thì bảng giữ nguyên
   * hình dạng cũ — cột chọn chỉ xuất hiện khi trang cha thật sự dùng.
   */
  selectedIds?: ReadonlySet<string>;
  onToggleSelect?: (item: InventoryItem) => void;
}

// ============================================
// Column Definitions
// ============================================

/**
 * Cột checkbox, chỉ dựng khi trang cha truyền handler chọn dòng.
 *
 * `stopPropagation` là bắt buộc: DataTable gắn onRowClick lên cả hàng, thiếu
 * nó thì tick chọn cũng mở luôn modal chi tiết.
 */
const createSelectColumn = (
  selectedIds: ReadonlySet<string>,
  onToggleSelect: (item: InventoryItem) => void
): Column<InventoryItem> => ({
  key: 'select',
  header: '',
  width: '44px',
  render: (row) => (
    <input
      type="checkbox"
      checked={selectedIds.has(row.id)}
      onChange={() => onToggleSelect(row)}
      onClick={(e) => e.stopPropagation()}
      className="h-4 w-4 cursor-pointer rounded border-slate-300 text-primary-600 focus:ring-primary-500"
    />
  ),
});

const createColumns = (t: (key: string) => string): Column<InventoryItem>[] => [
  {
    key: 'image',
    header: t('img'),
    width: '80px',
    render: (row) => (
      <InventoryCells.ImageCell imageUrl={row.imageUrl} productName={row.productName} />
    ),
  },
  {
    key: 'product',
    header: t('product'),
    width: '130px',
    render: (row) => (
      <InventoryCells.ProductCell name={row.productName} sku={row.sku} />
    ),
  },
  {
    key: 'address',
    // Bug #209: trước đây header này lấy chuỗi validation `storeIsRequired` rồi
    // `.replace(' is required', ...)` — mẹo chỉ khớp với bản tiếng Anh, nên ở
    // PL/UA tiêu đề cột hiện nguyên câu "Sklep jest wymagany". Dùng key riêng.
    header: t('storeWarehouseName'),
    width: '160px',
    render: (row) => (
      <InventoryCells.AddressCell address={row.address} shelf={row.shelfLocation} />
    ),
  },
  {
    key: 'quantity',
    header: t('quantity'),
    subHeader: '(Cur/Pre/Avail)',
    width: '150px',
    render: (row) => (
      <InventoryCells.QuantityCell
        current={row.currentQuantity}
        preOrdered={row.preOrderedQuantity}
        available={row.availableQuantity}
        max={Math.max(row.currentQuantity, 1)}
        status={row.status}
        minQuantity={row.minQuantity}
      />
    ),
  },
  {
    key: 'basePrice',
    header: t('basePrice'),
    width: '110px',
    render: (row) => (
      <InventoryCells.BasePriceCell basePrice={row.msrpPrice} />
    ),
  },
  {
    key: 'locationPrice',
    header: t('locationPrice'),
    width: '130px',
    render: (row) => (
      <InventoryCells.LocationPriceCell salePrice={row.salePrice} />
    ),
  },
  {
    key: 'cost',
    header: t('costPrice').replace(' Price', ''), // COST
    width: '80px',
    render: (row) => (
      <InventoryCells.CostCell cost={row.cost} />
    ),
  },
  {
    key: 'margin',
    header: t('margin'),
    width: '80px',
    render: (row) => (
      <InventoryCells.MarginCell margin={row.margin} />
    ),
  },
  {
    key: 'profitUnit',
    header: t('profitPerUnit'),
    width: '100px',
    render: (row) => (
      <InventoryCells.ProfitUnitCell profit={row.profit} />
    ),
  },
  {
    key: 'updateDate',
    header: 'UPDATED',
    width: '120px',
    render: (row) => (
      <InventoryCells.DateCell dateStr={row.updatedAt} />
    ),
  },
];

// ============================================
// Component
// ============================================

export function InventoryTable({
  data,
  isLoading,
  currentPage,
  totalPages,
  totalItems,
  pageSize,
  onPageChange,
  onRowClick,
  selectedIds,
  onToggleSelect,
}: InventoryTableProps) {
  const { t } = useI18n();
  const columns = createColumns(t);
  if (selectedIds && onToggleSelect) {
    columns.unshift(createSelectColumn(selectedIds, onToggleSelect));
  }

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <DataTable
        columns={columns}
        data={data}
        rowKey="id"
        isLoading={isLoading}
        onRowClick={onRowClick}
        showFilter={false}
        showHeaderMenu={false}
        emptyMessage={t('noOutofstockProductsInSelectedRange').replace('out-of-stock products', 'inventory items')}
        className="border-0 shadow-none"
      />

      {/* Pagination */}
      {!isLoading && totalPages > 0 && (
        <div className="border-t border-slate-100">
          <Pagination
            variant="numbered"
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={onPageChange}
            itemLabel="products"
          />
        </div>
      )}
    </div>
  );
}
